import { Tabs, TabsContent, TabsList, TabsTrigger } from "@workspace/web-ui/components/tabs";
import { MedicalPlan } from "../tab-content/medical-plan";
// import { Medication } from "../tab-content/medication";
import { PaymentHistory } from "../tab-content/payment-history";
import { Questionnaire } from "../tab-content/questionnaire";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  patientDetailsQueryOptions,
  // patientMedicalHistoryQueryOptions,
  patientMedicalPlanQueryOptions,
  // patientMedicationQueryOptions, // Medication tab is disabled
  patientNotesQueryOptions,
  patientPaymentHistoryQueryOptions,
  patientQuestionnaireQueryOptions,
  patientRefillsQueryOptions,
  patientSessionMedicalHistoryQueryOptions,
  patientSessionQueryOption,
  patientTimelineQueryOptions,
  patientVerificationQueryOptions,
} from "../../api/queries";
import {
  TimelineSkeleton,
  // MedicationSkeleton,
  MedicalPlanSkeleton,
  QuestionnaireSkeleton,
  PaymentHistorySkeleton,
  NotesSkeleton,
  RefillsSkeleton,
  VerificationSkeleton,
} from "../skeletons/tab-content-skeletons";
import { useState } from "react";
import NotesCard from "../card/notes-card";
// import { Overview } from "../tab-content/overview";
import { Sessions } from "../tab-content/session";
import { getRouteApi } from "@tanstack/react-router";
import ConditionsAllergiesCard from "../tab-content/conditions-allergies-card";
import NoPreviewCard from "../card/no-preview-card";
import { UserRoundIcon } from "lucide-react";
import { Refills } from "../tab-content/refils";
import { Verification } from "../tab-content/verification";

type PatientLogTabsProps = {
  userId: string;
  sessionId: string;
};

export const TAB_CONFIG = [
  { value: "timeline", label: "Sessions" },
  { value: "overview", label: "Medical History" },
  //   { value: "medication", label: "Medication" },
  //   { value: "questionnaire", label: "Questionnaire" },
  // { value: "medical-plan", label: "Medical Plan" },
  { value: "refills", label: "Refills" },
  { value: "payment-history", label: "Payment History" },
  { value: "notes", label: "Notes" },
  { value: "verification", label: "Verification" },
] as const;

export type TabValue = typeof TAB_CONFIG[number]["value"];

export const UrgentCarePatientLogTabs = ({ userId }: PatientLogTabsProps) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("timeline");
  const routeApi = getRouteApi("/(dashboard)/dashboard/$program");
  const { program } = routeApi.useParams();
  const { data: patientData} = useQuery(
    patientDetailsQueryOptions(userId, program ?? "")
  );

  const { data: sessionData, isLoading: isTimelineLoading } = useQuery(
    patientSessionQueryOption(userId, program ?? "")
  );

  console.log("sessionData", sessionData)

  const { data: historyData, isLoading: isHistoryLoading } = useQuery(
    patientSessionMedicalHistoryQueryOptions(userId, program ?? "")
  );

  // Prepare Important Questions data
  const importantData = historyData?.important_health_data ?? null;
  const importantEntries: Array<[string, unknown]> = importantData
    ? Object.entries(importantData).filter(([, v]) => v !== null && v !== undefined && v !== "")
    : [];
  const labelMap: Record<string, string> = {
    blood_pressure: "Blood Pressure",
    heart_rate: "Heart Rate",
    allergy_symptoms: "Allergy Symptoms",
    bp_bs_stable: "BP/BS Stable",
    palpitations: "Palpitations",
  };
  const formatLabel = (key: string): string =>
    labelMap[key] ?? key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const formatValue = (key: string, value: unknown): string => {
    if (key === "heart_rate") return `${value} bpm`;
    return String(value);
  };

  const { data: medicalPlanData, isLoading: isMedicalPlanLoading } = useQuery(
    patientMedicalPlanQueryOptions(userId, program ?? "")
  );

  const { data: questionnaireData, isLoading: isQuestionnaireLoading } = useQuery(
    patientQuestionnaireQueryOptions(userId, program)
  );

  const { data: refillsData, isLoading: isRefillsLoading } = useQuery(
    patientRefillsQueryOptions(userId, program)
  );

  const { data: paymentHistoryData, isLoading: isPaymentHistoryLoading } = useQuery(
    patientPaymentHistoryQueryOptions(userId, program)
  );

  const { data: notesData, isLoading: isNotesLoading } = useQuery(
    patientNotesQueryOptions(userId)
  );

  const { data: verificationData, isLoading: isVerificationLoading } = useQuery(
    patientVerificationQueryOptions(userId)
  );

  const prefetchTabData = (tabValue: string): void => {
    if (!userId) return;

    switch (tabValue) {
      case "overview":
        queryClient.prefetchQuery(patientSessionMedicalHistoryQueryOptions(userId, program));
        break;
      case "timeline":
        queryClient.prefetchQuery(patientTimelineQueryOptions(userId));
        break;
      case "refills":
        queryClient.prefetchQuery(patientRefillsQueryOptions(userId, program));
        break;
      // Medication tab is disabled in TAB_CONFIG, so prefetch is commented out
      // case "medication":
      //   queryClient.prefetchQuery(patientMedicationQueryOptions(userId, program, "active"));
      //   queryClient.prefetchQuery(patientMedicationQueryOptions(userId, program, "requests"));
      //   queryClient.prefetchQuery(patientMedicationQueryOptions(userId, program, "historic"));
      //   break;
      case "medical-plan":
        queryClient.prefetchQuery(patientMedicalPlanQueryOptions(userId, program ?? ""));
        break;
      case "payment-history":
        queryClient.prefetchQuery(patientPaymentHistoryQueryOptions(userId, program));
        break;
      case "notes":
        queryClient.prefetchQuery(patientNotesQueryOptions(userId));
        break;
      case "verification":
        queryClient.prefetchQuery(patientVerificationQueryOptions(userId));
        break;
    }
  };

  const handleTabChange = (value: string): void => {
    setActiveTab(value);
  };

  return (
    <Tabs defaultValue="overview" value={activeTab} className="w-full" onValueChange={handleTabChange}>
      <div className="w-full flex overflow-x-auto pb-1 sm:pb-2">
        <TabsList className="flex border-b no-scrollbar bg-transparent px-0 py-0 sm:py-1 whitespace-nowrap">
          {TAB_CONFIG.map(({ value, label }) => (
            <TabsTrigger
              key={value}
              value={value}
              className={`
                tab-trigger flex-shrink-0 text-xs sm:text-sm md:text-base whitespace-nowrap py-2
                ${activeTab === value ? "font-medium" : "font-normal"}
              `}
              onMouseEnter={() => prefetchTabData(value)}
            >
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>
      <div className="w-full mt-4">
        <TabsContent value="overview">
          {isHistoryLoading ? (
            <TimelineSkeleton />
          ) : (
            <div className="flex flex-col gap-4">
              {importantEntries.length > 0 ? (
                <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
                  <h1 className="text-lg font-semibold mb-2">Important Questions</h1>
                  <div className="grid grid-cols-[160px_1fr] gap-y-1">
                    {importantEntries.map(([key, value]) => (
                      <>
                        <span key={`${key}-label`} className="font-medium text-sm text-[#2D2F35]">
                          {formatLabel(key)}:
                        </span>
                        <span key={`${key}-value`} className="text-sm text-[#464B55]">
                          {formatValue(key, value)}
                        </span>
                      </>
                    ))}
                  </div>
                </div>
              ) : (
                <NoPreviewCard
                  Icon={UserRoundIcon}
                  title="Important Questions"
                  description="This patient has no answered important questions"
                  cardTitle="Important Questions"
                />
              )}
              <ConditionsAllergiesCard history={historyData ?? null} />
            </div>
          )}
        </TabsContent>
        <TabsContent value="timeline">
          {isTimelineLoading ? (
            <TimelineSkeleton />
          ) : (
            <Sessions sessionData={sessionData} />
          )}
        </TabsContent>
        {/* Medication tab is disabled in TAB_CONFIG, so queries and content are commented out */}
        {/* <TabsContent value="medication">
          {isActiveMedicationLoading || isRequestsMedicationLoading || isHistoryMedicationLoading || isPatientLoading ? (
            <MedicationSkeleton />
          ) : (
            patientData && activeMedicationData && requestsMedicationData && historyMedicationData && (
              <Medication
                activeMedicationData={activeMedicationData}
                requestsMedicationData={requestsMedicationData}
                historyMedicationData={historyMedicationData}
                medicalPlanData={medicalPlanData}
                patient={medicalPlanData ?? null}
                userId={patientData.userId}
                patientData={patientData}
                program={program ?? ""}
              />
            )
          )}
        </TabsContent> */}
        <TabsContent value="questionnaire">
          {isQuestionnaireLoading ? (
            <QuestionnaireSkeleton />
          ) : (
            <Questionnaire questionnaireData={questionnaireData ?? null} />
          )}
        </TabsContent>
        <TabsContent value="refills">
          {isRefillsLoading ? (
            <RefillsSkeleton />
          ) : (
            <Refills 
              refillsData={refillsData} 
              patientData={patientData}
             />
          )}
        </TabsContent>
        <TabsContent value="medical-plan">
          {isMedicalPlanLoading ? (
            <MedicalPlanSkeleton />
          ) : (
            <MedicalPlan medicalPlanData={medicalPlanData} userId={userId} />
          )}
        </TabsContent>
        <TabsContent value="payment-history">
          {isPaymentHistoryLoading ? (
            <PaymentHistorySkeleton />
          ) : (
            <PaymentHistory paymentHistoryData={paymentHistoryData} />
          )}
        </TabsContent>
        <TabsContent value="notes">
          {isNotesLoading ? (
            <NotesSkeleton />
          ) : (
            <NotesCard notesDataEncounter={notesData ?? null} />
          )}
        </TabsContent>
        <TabsContent value="verification">
          {isVerificationLoading ? (
            <VerificationSkeleton />
          ) : (
            <Verification verificationData={verificationData ?? null} userId={userId} />
          )}
        </TabsContent>
      </div>
    </Tabs>
  );
};
